import { useContext } from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  Abi,
  Address,
  Client,
  ContractFunctionName as ViemContractFunctionName,
  encodeFunctionData,
  EncodeFunctionDataParameters,
  erc20Abi,
} from "viem"
import {
  useEstimateGas,
  UseEstimateGasParameters,
  useGasPrice,
  useWaitForTransactionReceipt,
  useWalletClient,
  useWriteContract,
} from "wagmi"

import { ONE_MINUTE_IN_SECONDS, ONE_SECOND_IN_MILLISECONDS } from "../constants"
import { FlowContext } from "../context"

import { useAllowanceERC20Token } from "./token"

const useWriteContractAndWaitForTransaction = (chainId: number) => {
  const { queryKey, data: walletClient } = useWalletClient({ chainId })
  const queryClient = useQueryClient()

  const { isBitcoinFlow } = useContext(FlowContext)
  const config = isBitcoinFlow ? { getClient: () => walletClient! } : undefined

  const { data: txHash, ...rest } = useWriteContract({
    // @ts-expect-error We will wait for the wallet client in the `onMutate` fn
    // and we will check if is actually available.
    config,
    mutation: {
      onMutate: async () => {
        // Wait for the wallet client in case it hasn't been initialized yet.
        // This function will fire before the mutation function is fired.
        const cachedWalletClient = await queryClient.fetchQuery<
          Client | undefined
        >({
          queryKey,
        })

        if (!cachedWalletClient) {
          throw new Error("Wallet client not available")
        }
      },
    },
  })

  const { isLoading: isWaitingForTxReceipt, isSuccess: isTxConfirmed } =
    useWaitForTransactionReceipt({
      hash: txHash,
      chainId,
    })

  return {
    ...rest,
    isWaitingForTxReceipt,
    isTxConfirmed,
    txHash,
  }
}

export default useWriteContractAndWaitForTransaction

export function useEstimateTransactionGas<
  const ContractAbi extends Abi | readonly unknown[],
  ContractFunctionName extends
    | ViemContractFunctionName<ContractAbi>
    | undefined = undefined,
>(
  contract: EncodeFunctionDataParameters<ContractAbi, ContractFunctionName> & {
    address: Address
  },
  options: {
    chainId: number
    query: UseEstimateGasParameters["query"]
  },
) {
  const queryOptions = options.query ?? {}

  const {
    data: gasAmount,
    isFetching: isFetchingGasAmount,
    error,
  } = useEstimateGas({
    to: contract.address,
    data: encodeFunctionData(contract as EncodeFunctionDataParameters),
    chainId: options.chainId,
    query: options.query,
  })

  const { data: gasPrice, isFetching: isFetchingGasPrice } = useGasPrice({
    chainId: options.chainId,
    query: {
      enabled: !!gasAmount && !!queryOptions.enabled,
      refetchInterval: ONE_MINUTE_IN_SECONDS * ONE_SECOND_IN_MILLISECONDS,
      staleTime: ONE_MINUTE_IN_SECONDS * ONE_SECOND_IN_MILLISECONDS,
    },
  })

  const estimatedTransactionFee = (gasAmount ?? 0n) * (gasPrice ?? 0n)

  return {
    isFetching: isFetchingGasAmount || isFetchingGasPrice,
    gas: gasAmount,
    gasPrice,
    estimatedTransactionFee,
    error,
  }
}

export function useEstimateTransactionGasWithApprove<
  const ContractAbi extends Abi | readonly unknown[],
  ContractFunctionName extends
    | ViemContractFunctionName<ContractAbi>
    | undefined = undefined,
>(
  approveTxOptions: {
    tokenContractAddress: Address
    amountToSpent: bigint
    spender: Address
  },
  ...args: Parameters<
    typeof useEstimateTransactionGas<ContractAbi, ContractFunctionName>
  >
) {
  const [contract, options] = args

  const { data: approvedAmount, isFetching: isFetchingApprovedAmount } =
    useAllowanceERC20Token(
      approveTxOptions.tokenContractAddress,
      approveTxOptions.spender,
      options.chainId,
    )

  const {
    estimatedTransactionFee: estimatedApproveTransactionFee,
    isFetching: isFetchingApproveTransactionFee,
    gas: approveGas,
  } = useEstimateTransactionGas(
    {
      abi: erc20Abi,
      functionName: "approve",
      args: [approveTxOptions.spender, approveTxOptions.amountToSpent],
      address: approveTxOptions.tokenContractAddress,
    },
    {
      chainId: options.chainId,
      query: {
        enabled:
          approvedAmount !== undefined &&
          approvedAmount < approveTxOptions.amountToSpent,
      },
    },
  )

  const {
    estimatedTransactionFee: estimatedContractTransactionFee,
    isFetching: isFetchingContractTransactionFee,
    gasPrice,
    gas: transactionGas,
    // @ts-expect-error TODO how to fix this error.
  } = useEstimateTransactionGas(contract, options)

  const estimatedTransactionFee =
    estimatedApproveTransactionFee + estimatedContractTransactionFee

  const gasAmount = (approveGas ?? 0n) + (transactionGas ?? 0n)

  return {
    isFetching:
      isFetchingContractTransactionFee ||
      isFetchingApproveTransactionFee ||
      isFetchingApprovedAmount,
    gas: gasAmount,
    gasPrice,
    estimatedTransactionFee,
  }
}
